import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import SendbirdChannelPanel from "../../components/sendbird/SendbirdChannelPanel";
import { api } from "../../api/client";
import { Stethoscope, ChevronRight, MessagesSquare } from "lucide-react";

const STATUS_TONES = {
    requested: "bg-amber-100 text-amber-800",
    confirmed: "bg-blue-100 text-blue-700",
    in_progress: "bg-violet-100 text-violet-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-zinc-100 text-zinc-700",
};
const TABS = ["all", "requested", "confirmed", "in_progress", "completed", "cancelled"];

const AdminConsultations = () => {
    const [tab, setTab] = useState("confirmed");
    const [section, setSection] = useState("queue");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (tab !== "all") params.set("status", tab);
        api.get(`/admin/consultations?${params.toString()}`).then((r) => {
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
        }).finally(() => setLoading(false));
    };
    useEffect(load, [tab]); // eslint-disable-line

    return (
        <AdminAuthGuard>
            <AdminLayout title="Consultations" subtitle={`${total} consultation(s) match this view`}>
                <div className="mb-4 inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm" data-testid="admin-consults-section-tabs">
                    <button
                        onClick={() => setSection("queue")}
                        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                            section === "queue" ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid="doctor-queue-tab"
                    >
                        <Stethoscope className="h-3.5 w-3.5" /> Queue
                    </button>
                    <button
                        onClick={() => setSection("chats")}
                        className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                            section === "chats" ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid="doctor-live-consults-tab"
                    >
                        <MessagesSquare className="h-3.5 w-3.5" /> Live Consults
                    </button>
                </div>

                {section === "chats" ? (
                    <SendbirdChannelPanel
                        customType="doctor_consult"
                        claimEndpoint={() => "/sendbird/no-op"}
                        claimButtonLabel="Join"
                        showUnclaimedBadge={false}
                        testId="doctor-live-panel"
                        height="calc(100vh - 14rem)"
                    />
                ) : (
                <>
                <div className="mb-4 flex flex-wrap gap-1.5" data-testid="admin-consults-tabs">
                    {TABS.map((s) => (
                        <button
                            key={s}
                            onClick={() => setTab(s)}
                            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${tab === s ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 dark:bg-white/5 dark:text-zinc-300"}`}
                            data-testid={`admin-consults-tab-${s}`}
                        >
                            {s.replace("_", " ")}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <p className="text-sm text-zinc-500">Loading…</p>
                ) : items.length === 0 ? (
                    <p className="rounded-2xl border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500">No consultations match this view.</p>
                ) : (
                    <ul className="space-y-2" data-testid="admin-consults-list">
                        {items.map((c) => (
                            <li key={c.id}>
                                <Link
                                    to={`/admin/consultations/${c.id}`}
                                    className="flex items-center justify-between gap-3 rounded-2xl border border-zinc-100 bg-white p-4 transition-colors hover:border-brand-200 dark:bg-white/[0.03]"
                                    data-testid={`admin-consult-row-${c.id}`}
                                >
                                    <Stethoscope className="h-4 w-4 text-brand-700" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-zinc-900 dark:text-zinc-50">
                                            {c.doctor_name} <span className="text-xs text-zinc-500">· {(c.doctor_specialization || "").replace("-", " ")}</span>
                                        </p>
                                        <p className="mt-0.5 text-xs text-zinc-500 line-clamp-1">
                                            {c.patient_name || c.patient_email || "Patient"} · {new Date(c.scheduled_at).toLocaleString()} · {c.reason}
                                        </p>
                                    </div>
                                    <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_TONES[c.status] || "bg-zinc-100 text-zinc-700"}`}>{(c.status || "").replace("_", " ")}</span>
                                    <ChevronRight className="h-4 w-4 text-zinc-400" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
                </>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminConsultations;
